"use client"

import { useEffect, useRef, useState } from "react"
import { useInViewOnce } from "@/hooks/useInViewOnce"
import {
  renderRecaptcha,
  resetRecaptcha,
} from "@/lib/recaptcha/client"

interface SimpleReCaptchaProps {
  onVerify: (token: string) => void
  onError: (message: string) => void
  onExpired?: () => void
  className?: string
}

export default function SimpleReCaptcha({
  onVerify,
  onError,
  onExpired,
  className = "",
}: SimpleReCaptchaProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)
  const onVerifyRef = useRef(onVerify)
  const onErrorRef = useRef(onError)
  const onExpiredRef = useRef(onExpired)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState("")
  const [interacted, setInteracted] = useState(false)
  const nearViewport = useInViewOnce(containerRef, "200px")
  // reCAPTCHA is ~350 KB of JS; don't load it until the form is close to the
  // viewport or the user starts interacting with it.
  const shouldLoad = nearViewport || interacted

  useEffect(() => {
    const form = containerRef.current?.closest("form")
    if (!form || interacted) return
    const activate = () => setInteracted(true)
    form.addEventListener("focusin", activate, { once: true })
    form.addEventListener("pointerdown", activate, { once: true })
    return () => {
      form.removeEventListener("focusin", activate)
      form.removeEventListener("pointerdown", activate)
    }
  }, [interacted])

  useEffect(() => {
    onVerifyRef.current = onVerify
    onErrorRef.current = onError
    onExpiredRef.current = onExpired
  }, [onVerify, onError, onExpired])

  useEffect(() => {
    if (!shouldLoad) return
    let mounted = true

    const initRecaptcha = async () => {
      if (!containerRef.current || widgetIdRef.current !== null) {
        return
      }

      try {
        const widgetId = await renderRecaptcha(containerRef.current, {
          callback: (token: string) => {
            onVerifyRef.current(token)
            setLoadError("")
          },
          "expired-callback": () => {
            onExpiredRef.current?.()
            onErrorRef.current("reCAPTCHA expired. Please complete it again.")
          },
          "error-callback": () => {
            setLoadError("reCAPTCHA failed to load. Please refresh and try again.")
          },
        })

        if (!mounted) {
          return
        }

        widgetIdRef.current = widgetId
        setIsLoading(false)
      } catch (error) {
        if (!mounted) {
          return
        }

        setIsLoading(false)
        setLoadError("Failed to initialize reCAPTCHA.")
      }
    }

    initRecaptcha()

    return () => {
      mounted = false
      if (widgetIdRef.current !== null) {
        resetRecaptcha(widgetIdRef.current)
      }
    }
  }, [shouldLoad])

  return (
    <div className={className}>
      <div ref={containerRef} className="min-h-[78px]" />
      {loadError ? (
        <p className="mt-2 text-xs text-red-300">{loadError}</p>
      ) : null}
      {shouldLoad && isLoading ? (
        <p className="mt-2 text-xs text-neutral-200">Loading reCAPTCHA...</p>
      ) : null}
    </div>
  )
}
